import { useState } from "react";
import { AdminPageHeader } from "@/components/admin-page-header";
import {
	getGetOpportunityByIdQueryKey,
	getGetOpportunityByIdQueryOptions,
	getGetOpportunitySharesQueryKey,
	getGetOpportunitySharesQueryOptions,
	getGetOpportunityClaimsQueryKey,
	getGetOpportunityClaimsQueryOptions,
	useShareOpportunity,
	getListTalentMembersQueryOptions,
} from "@fp/admin-api-client/client";
import { Button } from "@fp/ui/components/base/buttons/button";
import {
	PageContentContainer,
	PageLoading,
	PageProgressBar,
} from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";
import { OpportunityDetail } from "@fp/ui/components/opportunities/opportunity-detail";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "@untitledui/icons";
import { toast } from "sonner";

export const Route = createFileRoute("/opps/$oppId/edit/detail")({
	component: RouteComponent,
	loader: async ({ params, context: { queryClient } }) => {
		const [opportunity, oppShares, oppClaims, talentMembersResponse] = await Promise.all([
			queryClient.fetchQuery(getGetOpportunityByIdQueryOptions(params.oppId)),
			queryClient.fetchQuery(getGetOpportunitySharesQueryOptions(params.oppId)),
			queryClient.fetchQuery(getGetOpportunityClaimsQueryOptions(params.oppId)),
			queryClient.fetchQuery(
				getListTalentMembersQueryOptions({ page: 1, pageSize: 100 }),
			),
		]);

		return {
			opportunity: opportunity.data,
			shares: oppShares.data.items || [],
			claims: oppClaims.data.items || [],
			talentMembers: talentMembersResponse.data.items || [],
		};
	},
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const { opportunity, shares, claims, talentMembers } = Route.useLoaderData();
	const router = useRouter();
	const params = Route.useParams();
	const queryClient = useQueryClient();
	const shareMutation = useShareOpportunity();
	const [showNoTalentMembersModal, setShowNoTalentMembersModal] = useState(false);

	const handleShare = async () => {
		await shareMutation.mutateAsync(
			{ id: params.oppId },
			{
				onSuccess: async () => {
					await queryClient.invalidateQueries({
						queryKey: getGetOpportunityByIdQueryKey(params.oppId),
					});
					await queryClient.invalidateQueries({
						queryKey: getGetOpportunitySharesQueryKey(params.oppId),
					});
					await router.invalidate();
					toast.success(`Shared "${opportunity.title}"`);
					router.navigate({ to: "/opps" });
				},
			},
		);
	};

	const onShare = async () => {
		if (talentMembers.length === 0) {
			setShowNoTalentMembersModal(true);
			return;
		}
		await handleShare();
	};

	const onInvite = () => {
		setShowNoTalentMembersModal(false);
		router.navigate({
			to: "/opps/$oppId/edit/invite",
			params: { oppId: params.oppId },
		});
	};

	const onShareFromModal = async () => {
		setShowNoTalentMembersModal(false);
		await handleShare();
	};

	return (
		<AdminLayout>
			<AdminPageHeader
				title="Opp Preview"
				parent={{
					url: router.buildLocation({
						to: "/opps/$oppId/edit/proof-of-work",
						params: { oppId: params.oppId },
					}).href,
					label: "Proof of Work",
				}}
				footer={<PageProgressBar current={5} total={4} />}
			/>
			<PageContentContainer className="space-y-6 p-0">
				<OpportunityDetail
					{...opportunity}
					shares={shares}
					claims={claims}
					isEdit={true}
					onRemoveTalentMember={async () => {
						await queryClient.invalidateQueries({
							queryKey: getGetOpportunityByIdQueryKey(params.oppId),
						});
						await queryClient.invalidateQueries({
							queryKey: getGetOpportunitySharesQueryKey(params.oppId),
						});
						await queryClient.invalidateQueries({
							queryKey: getGetOpportunityClaimsQueryKey(params.oppId),
						});
						await router.invalidate();
					}}
					onAddTalentMember={() => {
						router.navigate({
							to: "/opps/$oppId/edit/invite",
							params: { oppId: params.oppId },
							state: { returnTo: `/opps/${params.oppId}/edit/detail` },
						});
					}}
				/>
				<div className="flex-1 flex items-end pt-6 px-4 pb-6">
					<Button
						type="button-primary"
						onClick={onShare}
						isDisabled={shareMutation.isPending}
						className="h-16 w-full"
					>
						{shareMutation.isPending ? "Sharing..." : "Share"}
					</Button>
				</div>
			</PageContentContainer>
			{showNoTalentMembersModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg w-full max-w-md mx-4">
						<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
							<h3 className="text-lg font-semibold text-gray-900">
								Add Talent Members First?
							</h3>
							<Button
								color="tertiary"
								size="sm"
								onClick={() => setShowNoTalentMembersModal(false)}
							>
								<X className="size-5" />
							</Button>
						</div>
						<div className="p-4">
							<p className="text-sm text-gray-600">
								You have not added any talent members yet. Would you like to do
								that before sharing?
							</p>
						</div>
						<div className="flex gap-3 px-4 py-3 border-t border-gray-200">
							<Button
								color="secondary"
								className="flex-1"
								onClick={onShareFromModal}
							>
								Share
							</Button>
							<Button color="primary" className="flex-1" onClick={onInvite}>
								Invite
							</Button>
						</div>
					</div>
				</div>
			)}
		</AdminLayout>
	);
}
